import { useState } from 'react';

import {
  Button,
  Checkbox,
  Flex,
  VStack,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverFooter,
  PopoverButtonGroup,
  PopoverTrigger,
  Portal,
  GearIcon,
  type FlexProps,
} from '@frontend/legacy-components';

type Option = {
  id: string;
  label: string;
  value: string;
};
interface DisplayOptionsProps extends FlexProps {
  disabledOptions: string[];
  options: {
    showSubValueLabel?: string;
    showSubValue?: boolean;
    truncateValuesLabel?: string;
    truncateValues?: boolean;
  };
  cellTypeOptions: {
    cellTypes: Option[];
    value: string;
  };
  additionalOptions: Option[];
  onOptionsChange: (
    options: Record<string, boolean>,
    cellTypeValue: string,
  ) => void;
  disabled?: boolean;
}

const DisplayOptions = ({
  disabledOptions,
  options,
  cellTypeOptions,
  additionalOptions = [],
  onOptionsChange,
  disabled,
  ...rest
}: DisplayOptionsProps) => {
  const checkboxData = [
    {
      id: 'showSubValue',
      label: options?.showSubValueLabel ?? 'Show unit values',
      value: options?.showSubValue ?? true,
    },
    {
      id: 'truncateValues',
      label: options?.truncateValuesLabel ?? 'Truncate values',
      value: options?.truncateValues ?? false,
    },
    ...additionalOptions.map((option) => ({
      ...option,
      value: option.value ?? false,
    })),
  ].filter(({ id }) => !disabledOptions?.includes(id));
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [cellTypeValue, setCellTypeValue] = useState(cellTypeOptions?.value);
  const [displayOptionsValues, setDisplayOptionsValues] = useState<
    Record<string, boolean>
  >(
    checkboxData.reduce(
      (acc, option) => ({ ...acc, [option.id]: option.value }),
      {},
    ),
  );

  const openPopover = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setDisplayOptionsValues(
      checkboxData.reduce(
        (acc, option) => ({ ...acc, [option.id]: option.value }),
        {},
      ),
    );
    setCellTypeValue(cellTypeOptions?.value);
    setIsPopoverOpen(true);
  };
  const closePopover = () => setIsPopoverOpen(false);

  return (
    <Popover
      size="sm"
      placement="bottom-end"
      isOpen={isPopoverOpen}
      onClose={closePopover}
    >
      <Flex justifyContent="flex-end" {...rest}>
        <PopoverTrigger>
          <IconButton isDisabled={disabled} onClick={openPopover}>
            <GearIcon boxSize={3} />
          </IconButton>
        </PopoverTrigger>
      </Flex>
      <Portal>
        <PopoverContent onClick={(e) => e.stopPropagation()}>
          <PopoverHeader>Display Options</PopoverHeader>
          <PopoverBody>
            <VStack spacing={2}>
              <FormControl>
                <FormLabel>Display</FormLabel>
                <VStack spacing={1} alignItems="flex-start">
                  {checkboxData.map((option) => (
                    <Checkbox
                      key={option.id}
                      isChecked={displayOptionsValues[option.id]}
                      onChange={(e) =>
                        setDisplayOptionsValues({
                          ...displayOptionsValues,
                          [option.id]: e.target.checked,
                        })
                      }
                    >
                      {option.label}
                    </Checkbox>
                  ))}
                </VStack>
              </FormControl>
              {cellTypeOptions?.cellTypes && (
                <FormControl>
                  <FormLabel>Cell Type</FormLabel>
                  <RadioGroup value={cellTypeValue} onChange={setCellTypeValue}>
                    {cellTypeOptions.cellTypes.map((cellType) => (
                      <Radio key={cellType.value} value={cellType.value}>
                        {cellType.label}
                      </Radio>
                    ))}
                  </RadioGroup>
                </FormControl>
              )}
            </VStack>
          </PopoverBody>
          <PopoverFooter>
            <PopoverButtonGroup>
              <Button variant="secondary" onClick={closePopover}>
                Cancel
              </Button>
              <Button
                onClick={() => {
                  onOptionsChange?.(displayOptionsValues, cellTypeValue);
                  closePopover();
                }}
              >
                Apply
              </Button>
            </PopoverButtonGroup>
          </PopoverFooter>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

export default DisplayOptions;
